import {Component, Injector, OnInit} from '@angular/core';
import {Message} from "../../../models/message";
import {ActivatedRoute, Router} from "@angular/router";
import {MessageService} from "../../../services/message/message.service";
import {MessageRecipient, MessageRecipients, MessagesStateService} from "../../../services/message/message.state.service";
import {BaseComponent} from "../../base/base.component";
import {UserService} from "../../../services/user/user.service";
import {User} from "../../../models/user";
import * as moment from "moment";
import {DomSanitizer, SafeHtml} from "@angular/platform-browser";

@Component({
    selector: 'ui-message-details',
    templateUrl: './message-details.component.html'
})
export class MessageDetailsComponent extends BaseComponent implements OnInit {

    message: Message | undefined;
    messageBody: SafeHtml = '';
    sender: User | undefined;
    isSent = false;
    recipients: MessageRecipients | undefined;
    recipientsFilter: 'unread' | 'read' = 'unread';

    constructor(
        public route: ActivatedRoute,
        public router: Router,
        public messageService: MessageService,
        public userService: UserService,
        private messagesStateService: MessagesStateService,
        private sanitizer: DomSanitizer,
        public serviceInjector: Injector,
        ) {
        super(serviceInjector);
    }


    async ngOnInit() {
        const messageId = this.route.snapshot.params['messageId'];
        if (!messageId || !this.isUUID(messageId)) {
            this.router.navigate(['/users/messages']);
            return;
        }

        // The list already holds the full message: render it instantly, fetch only when opened directly
        const message = this.messagesStateService.findMessage(messageId)
            ?? (await this.messageService.getMessageById(messageId))?.data;
        if (!message) {
            this.previousPage();
            return;
        }
        this.message = message;
        this.messageBody = this.sanitizer.bypassSecurityTrustHtml(message.text);

        this.isSent = message.createdById === localStorage.getItem('wisper_userId');
        if (this.isSent) void this.loadRecipients(messageId);

        const senders = this.messagesStateService.senders;
        this.sender = senders.get(message.createdById!);
        if (!this.sender) {
            this.sender = await this.userService.getUserById(message.createdById!);
            senders.set(message.createdById!, this.sender);
        }
    }

    async loadRecipients(messageId: string) {
        const cache = this.messagesStateService.recipients;
        this.recipients = cache.get(messageId);
        const response = await this.messageService.getMessageRecipients(messageId);
        if (response?.success) {
            this.recipients = response.data;
            cache.set(messageId, response.data);
            if (!this.unreadRecipients.length) this.recipientsFilter = 'read';
        }
    }

    get readRecipients(): MessageRecipient[] {
        return this.recipients?.recipients.filter((recipient) => recipient.seen) ?? [];
    }

    get unreadRecipients(): MessageRecipient[] {
        return this.recipients?.recipients.filter((recipient) => !recipient.seen) ?? [];
    }

    get readPercent(): number {
        const total = this.recipients?.recipients.length ?? 0;
        return total ? Math.round((this.readRecipients.length / total) * 100) : 0;
    }

    initials(firstName: string, lastName: string): string {
        return `${firstName?.charAt(0) ?? ''}${lastName?.charAt(0) ?? ''}`.toUpperCase() || '?';
    }

    get senderInitials(): string {
        return `${this.sender?.firstName?.charAt(0) ?? ''}${this.sender?.lastName?.charAt(0) ?? ''}`.toUpperCase() || '?';
    }

    received(createdAt: string | undefined): string {
        const days = moment().startOf('day').diff(moment(createdAt).startOf('day'), 'days');
        if (days === 0) return 'Oggi';
        if (days === 1) return 'Ieri';
        if (days <= 7) return `${days} giorni fa`;
        return '';
    }

    formatMessageDate(date: string) {
        return moment(date).format('DD MMM YYYY, HH:mm');
    }
}
